import { assembleNameWithEmailFallback, joinDefinedStrings, TextWithEllipsis, tokens, User } from 'ComponentLibrary'
import { ReactNode } from 'react'
import { ExistingMessage, SystemMessage } from '../molecules/Message/types'
import { mentionPattern } from '../molecules/TextEditor/TextEditor'

type ThreadPreviewMessage =
    | Pick<ExistingMessage, 'message_type' | 'content' | 'user_name' | 'author_type' | 'user_id' | 'external_participant_id'>
    | Pick<SystemMessage, 'message_type' | 'content' | 'author_type'>

export type Thread = {
    uuid: string
    topic: string
    user_id?: number
    user_name?: string
    participant_users: User[]
    messages: ThreadPreviewMessage[]
    created_on: string
    updated_on: string
}

export type ThreadType = 'chat' | 'task' | 'system'

// To not pollute the component library with business logic for task I just allow it to be unknown and check for undefinededness
export type ThreadWithMetadata<TaskType> = Thread & { associatedTask?: TaskType } & { isPinned?: boolean }

export type ThreadCopy = {
    noMessages: string
    deletedMessage: string
    createdBy: (userName: string) => string
    createdAutomatically: string
}

export const DEFAULT_THREAD_COPY: ThreadCopy = {
    noMessages: 'Keine Nachrichten',
    deletedMessage: 'Nachricht wurde gelöscht',
    createdBy: (userName) => `Unterhaltung wurde von ${userName} angelegt`,
    createdAutomatically: 'Die Unterhaltung wurde automatisch angelegt',
}

export function getThreadType<T>(thread: ThreadWithMetadata<T>): ThreadType {
    // We define a system thread as to be a thread where the first message is a system message
    if (thread.messages?.[0]?.message_type === 'system_message') {
        return 'system'
    }
    return thread.associatedTask !== undefined ? 'task' : 'chat'
}

const MESSAGE_PREVIEW_TYPOGRAPHY_PROPS = { variant: 'bodyTextSmall' as const, color: tokens.ColorTextDefault }

export function simplifyMessagePreviewContent(content: string): string {
    return content
        .replace(/\r\n?/g, '\n') // Normalize line endings: "\r\n" -> "\n"
        .replace(/^(#+)\s+(.+)/gm, '') // Remove heading markers: "### Titel" -> "Titel"
        .replace(/(\*\*|__)([\s\S]+?)\1/g, '$2') // Remove bold markers: "**x**"/"__x__" -> "x"
        .replace(/(^|[\s([{"'])\*([^*\n]+)\*(?=$|[\s)\]}",'.!?;:])/g, '$1$2') // Remove italic stars: "*x*" -> "x"
        .replace(/\s*\n+\s*/g, ' ') // Collapse all line breaks to one space
}

export function getMessagePreviewForThread(thread: Thread, copy?: Partial<ThreadCopy>): ReactNode {
    const resolvedCopy = { ...DEFAULT_THREAD_COPY, ...copy }
    const message = thread.messages.at(-1)
    if (!message) {
        const threadPreview = resolvedCopy.noMessages
        return (
            <TextWithEllipsis
                tooltipText={threadPreview}
                typographyProps={{ ...MESSAGE_PREVIEW_TYPOGRAPHY_PROPS, style: { fontStyle: 'italic' } }}
            >
                {threadPreview}
            </TextWithEllipsis>
        )
    }

    const threadUserName = message.message_type === 'system_message' ? undefined : message.user_name
    const threadContentPreview = message.message_type === 'deleted_message' ? resolvedCopy.deletedMessage : message.content

    const textWithBeautifiedMentions = threadContentPreview.replace(mentionPattern, (_, id) => {
        const mentionedThreadParticipant = thread.participant_users.find((user) => user.id === parseInt(id))

        return `@${mentionedThreadParticipant ? assembleNameWithEmailFallback(mentionedThreadParticipant) : '?'}`
    })

    const threadPreviewContent = simplifyMessagePreviewContent(textWithBeautifiedMentions)
    const threadPreview = joinDefinedStrings([threadUserName, threadPreviewContent], ': ')
    return (
        <TextWithEllipsis tooltipText={threadPreview} typographyProps={MESSAGE_PREVIEW_TYPOGRAPHY_PROPS}>
            {threadPreview}
        </TextWithEllipsis>
    )
}

export function getThreadCreatedByInfo<T>(thread: ThreadWithMetadata<T>, copy?: Partial<ThreadCopy>) {
    const resolvedCopy = { ...DEFAULT_THREAD_COPY, ...copy }
    let threadUserName: string | undefined = thread.user_name
    if (thread.associatedTask && thread.messages.length > 0 && thread.messages[0].message_type !== 'system_message') {
        threadUserName = thread.messages[0].user_name
    }
    const threadCreatedBy = threadUserName ? resolvedCopy.createdBy(threadUserName) : resolvedCopy.createdAutomatically
    return threadCreatedBy
}
